package org.example.site_web.components.sections

import androidx.compose.runtime.*
import com.varabyte.kobweb.browser.dom.ElementTarget
import com.varabyte.kobweb.compose.css.CSSLengthNumericValue
import com.varabyte.kobweb.compose.css.ScrollSnapAlign
import com.varabyte.kobweb.compose.css.StyleVariable
import com.varabyte.kobweb.compose.css.TextDecorationLine
import com.varabyte.kobweb.compose.css.WhiteSpace
import com.varabyte.kobweb.compose.css.functions.CSSMax
import com.varabyte.kobweb.compose.css.functions.CSSMin
import com.varabyte.kobweb.compose.css.functions.blur
import com.varabyte.kobweb.compose.css.functions.clamp
import com.varabyte.kobweb.compose.css.functions.min
import com.varabyte.kobweb.compose.css.functions.saturate
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.foundation.layout.RowDefaults.VerticalAlignment
import com.varabyte.kobweb.compose.foundation.layout.Spacer
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.graphics.Color
import com.varabyte.kobweb.compose.ui.graphics.Colors
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.silk.components.icons.CloseIcon
import com.varabyte.kobweb.silk.components.icons.HamburgerIcon
import com.varabyte.kobweb.silk.components.icons.MoonIcon
import com.varabyte.kobweb.silk.components.icons.SunIcon
import com.varabyte.kobweb.silk.components.navigation.Link
import com.varabyte.kobweb.silk.components.overlay.Overlay
import com.varabyte.kobweb.silk.components.overlay.OverlayVars
import com.varabyte.kobweb.silk.components.overlay.PopupPlacement
import com.varabyte.kobweb.silk.components.overlay.Tooltip
import com.varabyte.kobweb.silk.init.InitSilk
import com.varabyte.kobweb.silk.init.InitSilkContext
import com.varabyte.kobweb.silk.style.CssStyle
import com.varabyte.kobweb.silk.style.animation.Keyframes
import com.varabyte.kobweb.silk.style.animation.toAnimation
import com.varabyte.kobweb.silk.style.breakpoint.Breakpoint
import com.varabyte.kobweb.silk.style.breakpoint.displayIfAtLeast
import com.varabyte.kobweb.silk.style.breakpoint.displayUntil
import com.varabyte.kobweb.silk.style.common.SmoothColorStyle
import com.varabyte.kobweb.silk.style.extendedByBase
import com.varabyte.kobweb.silk.style.selectors.active
import com.varabyte.kobweb.silk.style.selectors.hover
import com.varabyte.kobweb.silk.style.selectors.link
import com.varabyte.kobweb.silk.style.selectors.visited
import com.varabyte.kobweb.silk.style.toModifier
import com.varabyte.kobweb.silk.theme.colors.ColorMode
import org.example.site_web.aiPalette
import org.example.site_web.components.widgets.IconButton
import org.example.site_web.toSitePalette
import org.jetbrains.compose.web.dom.A
import org.jetbrains.compose.web.dom.Img

import com.varabyte.kobweb.compose.ui.graphics.Color.Rgb
import com.varabyte.kobweb.silk.components.forms.Button
import com.varabyte.kobweb.silk.components.forms.CheckboxKind
import com.varabyte.kobweb.silk.components.icons.ArrowUpIcon
import com.varabyte.kobweb.silk.components.icons.ChevronDownIcon
import com.varabyte.kobweb.silk.components.icons.ChevronUpIcon
import com.varabyte.kobweb.silk.style.until
import kotlinx.browser.window
import org.example.site_web.initColorMode
import org.jetbrains.compose.web.css.AnimationDirection
import org.jetbrains.compose.web.css.AnimationFillMode
import org.jetbrains.compose.web.css.AnimationTimingFunction
import org.jetbrains.compose.web.css.cssRem
import org.jetbrains.compose.web.css.ms
import org.jetbrains.compose.web.css.percent
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.css.vw

import org.jetbrains.compose.web.css.CSSColorValue
import org.jetbrains.compose.web.css.LineStyle
import org.jetbrains.compose.web.css.Position
import org.jetbrains.compose.web.css.em
import org.jetbrains.compose.web.css.vh
import org.jetbrains.compose.web.dom.Text

private fun getNavBackgroundColor(colorMode: ColorMode): Rgb {
    return when (colorMode) {
        ColorMode.DARK -> colorMode.toSitePalette().nearBackground.toRgb()
        ColorMode.LIGHT -> colorMode.toSitePalette().nearBackground.toRgb()
    }.copyf(alpha = 0.65f)
}

val NavHeaderHeight by StyleVariable<CSSLengthNumericValue>()
val DividerColor by StyleVariable<CSSColorValue>()

val NavHeaderBackgroundStyle = SmoothColorStyle.extendedByBase {
    Modifier
        .backgroundColor(getNavBackgroundColor(colorMode))
        .backdropFilter(saturate(180.percent), blur(6.px))
        //.boxShadow(spreadRadius = 1.px, color = DividerColor.value())
}

val NavHeaderDarkenedBackgroundStyle = NavHeaderBackgroundStyle.extendedByBase {
    Modifier
        .backgroundColor(getNavBackgroundColor(colorMode).copyf(alpha = 0.7f))
}

val NavHeaderStyle = NavHeaderDarkenedBackgroundStyle.extendedByBase {
    Modifier
        .fillMaxWidth()
        .position(Position.Sticky)
        .top(0.percent)
        .height(NavHeaderHeight.value())
}

@InitSilk
fun initNavHeaderHeight(ctx: InitSilkContext) = with(ctx.stylesheet) {
    registerStyle("html") {
        base { Modifier.setVariable(NavHeaderHeight, 7.cssRem) }
        Breakpoint.MD { Modifier.setVariable(NavHeaderHeight, 7.cssRem) }
    }
}

val MenuHeaderStyle = CssStyle{
    base {
        Modifier
            .whiteSpace(WhiteSpace.NoWrap)
            .fontSize(1.0.cssRem)
            .color(Colors.White)
    }

    link {
        Modifier.color(Colors.White)
    }
    visited {
        Modifier.color(Colors.White)
    }

    hover {
        Modifier.color(aiPalette.mainPalette.primary)
            .textDecorationLine(TextDecorationLine.Underline)
    }

    active {
        Modifier.color(Colors.White)
    }
}

val LogoStyle = CssStyle{
    base{
        Modifier
        .height(3.cssRem)
        .margin(0.px)
        .padding(left = 2.cssRem, top = 0.cssRem, bottom = 0.cssRem)
    }
    until(Breakpoint.MD) {
        Modifier
        .height(2.5.cssRem)
        .margin(0.px)
        .padding(left = 1.cssRem, top = 0.cssRem, bottom = 0.cssRem) }
}



@Composable
private fun NavLink(path: String, text: String, isMajor: Boolean = false) {
    Link(path, text,
        modifier = MenuHeaderStyle.toModifier().fillMaxWidth())
}

@Composable
private fun MenuItems() {
    NavLink("/offers", "Notre Offre")
    NavLink("/projects", "Projet Ind&ffi")
    NavLink("/who-are-we", "Qui sommes-nous?")
}

@Composable
private fun ColorModeButton() {
    var colorMode by ColorMode.currentState
    IconButton(onClick = { colorMode = colorMode.opposite },) {
        if (colorMode.isLight) MoonIcon() else SunIcon()
    }
    Tooltip(ElementTarget.PreviousSibling, "Toggle color mode", placement = PopupPlacement.BottomRight)
}

@Composable
private fun HamburgerButton(onClick: () -> Unit) {
    IconButton(onClick) {
        HamburgerIcon()
    }
}

@Composable
private fun CloseButton(onClick: () -> Unit) {
    IconButton(onClick) {
        CloseIcon()
    }
}

val SideMenuSlideInAnim = Keyframes {
    from {
        Modifier.translateX(100.percent)
    }

    to {
        Modifier
    }
}

// Note: When the user closes the side menu, we don't immediately stop rendering it (at which point it would disappear
// abruptly). Instead, we start animating it out and only stop rendering it when the animation is complete.
enum class SideMenuState {
    CLOSED,
    OPEN,
    CLOSING;

    fun close() = when (this) {
        CLOSED -> CLOSED
        OPEN -> CLOSING
        CLOSING -> CLOSING
    }
}

@Composable
fun NavHeader() {

    val buttonBackVisibility = remember { mutableStateOf(false) }

    LaunchedEffect(Unit) {
        window.addEventListener("scroll",{evt->
            if(window.scrollY > 100){
                buttonBackVisibility.value = true
            }
            else{
                buttonBackVisibility.value = false
            }
            //console.log("Scrolled :" + window.scrollY)
        })
    }


    Row(NavHeaderStyle.toModifier()
        .fillMaxWidth()
        .position(Position.Sticky)
        .top(0.px)
        .zIndex(2)
        .borderBottom {
            color(Colors.Gray)
            width(1.px)
            style(if (buttonBackVisibility.value) {
                LineStyle.Solid}
                else{
                    LineStyle.None
            })
        },
        verticalAlignment = Alignment.CenterVertically) {

            Link(path="/",modifier = LogoStyle.toModifier()
                ) {
                Img(src = "/assets/logo_accelarge_color_nobckg.svg",
                    alt="Accelarge Innovations",
                    attrs = Modifier.fillMaxSize().toAttrs())
            }

            Spacer()

            Row(
                Modifier.gap(1.5.cssRem)
                    .displayIfAtLeast(Breakpoint.MD)
                    .padding(leftRight = 2.5.cssRem)
                    .color(Colors.White), verticalAlignment = Alignment.CenterVertically
            ) {
                MenuItems()
            }
            var menuState by remember { mutableStateOf(SideMenuState.CLOSED) }

            Row(
                Modifier
                    .margin(right = 1.0.cssRem)
                    .fontSize(1.5.cssRem)
                    .gap(1.cssRem)
                    .displayUntil(Breakpoint.MD),
                verticalAlignment = Alignment.Top,
            ) {
                HamburgerButton(onClick = { menuState = SideMenuState.OPEN })
                if (menuState != SideMenuState.CLOSED) {
                    SideMenu(
                        menuState,
                        close = { menuState = menuState.close() },
                        onAnimationEnd = {
                            if (menuState == SideMenuState.CLOSING) menuState = SideMenuState.CLOSED
                        }
                    )
                }
            }

        //Bouton pour remonter...
        if(buttonBackVisibility.value) {
            A(
                href = "#",
                attrs = Modifier
                    .position(Position.Absolute)
                    .top(85.vh)
                    .left(min(5.vw, 1.em))
                    .width(3.em)
                    .height(3.em)
                    .backgroundColor(Colors.Black)
                    .borderRadius(1.em)
                    .border {
                        style(LineStyle.Solid)
                        color(Colors.Gray)
                        width(2.px)
                    }.toAttrs()
            ) {
                Box(
                    Modifier
                        .fillMaxSize(),
                    contentAlignment = Alignment.Center
                ) {
                    ChevronUpIcon(
                        Modifier
                            .width(2.em)
                            .height(2.em)
                            .color(ColorMode.current.toSitePalette().brand.primary)
                    )
                }
            }
        }
    }
}

@Composable
private fun SideMenu(menuState: SideMenuState, close: () -> Unit, onAnimationEnd: () -> Unit) {
    Overlay(
        Modifier
            .displayUntil(Breakpoint.MD)
            .setVariable(OverlayVars.BackgroundColor, Colors.Transparent)
            .zIndex(2)
            .onClick { close() }
    ) {
        key(menuState) { // Force recompute animation parameters when close button is clicked
            Column(
                Modifier
                    .height(16.cssRem)
                    .width(clamp(8.cssRem, 50.percent, 12.cssRem))
                    .align(Alignment.TopEnd)
                    // Close button will appear roughly over the hamburger button, so the user can close
                    // things without moving their finger / cursor much.
                    .padding(topBottom = 1.cssRem, leftRight = 0.cssRem)
                    .backgroundColor(ColorMode.current.toSitePalette().farBackground)
                    .color(ColorMode.current.toSitePalette().brand.primary)
                    .animation(
                        SideMenuSlideInAnim.toAnimation(
                            duration = 200.ms,
                            timingFunction = if (menuState == SideMenuState.OPEN) AnimationTimingFunction.EaseOut else AnimationTimingFunction.EaseIn,
                            direction = if (menuState == SideMenuState.OPEN) AnimationDirection.Normal else AnimationDirection.Reverse,
                            fillMode = AnimationFillMode.Forwards
                        )
                    )
                    .borderRadius(bottomLeft = 1.cssRem)
                    .borderLeft {
                        color(Colors.Gray)
                        width(2.px)
                        style(LineStyle.Solid)
                    }
                    .borderBottom {
                        color(Colors.Gray)
                        width(2.px)
                        style(LineStyle.Solid)
                    }
                    .onClick { it.stopPropagation() }
                    .onAnimationEnd { onAnimationEnd() },
                horizontalAlignment = Alignment.Start
            ) {
                Box(Modifier
                    .fillMaxWidth()
                    .padding(right = 1.cssRem),
                    contentAlignment = Alignment.CenterEnd){
                    CloseButton(onClick = { close() })
                }
                Column(Modifier
                    .margin(1.cssRem)
                    .padding(topBottom = 0.cssRem)
                    .gap(1.cssRem), horizontalAlignment = Alignment.CenterHorizontally) {
                    MenuItems()
                }
            }
        }
    }
}
